// Ottelumuodot (Teams / sarjaottelut). docs/features/teams.md §4.2.
//
// A format is a bill of materials, not a timetable: it says which rubbers a tie may contain and
// how many of each, and nothing at all about when they are played or on which court. Layout is
// the home team's call.
//
// KEEP THIS FILE FREE OF REACT AND FIREBASE. remainingOffer() is the guard the Phase E backend
// needs as well as the menu the Phase F wizard renders; if the two sides reimplement it
// separately they will disagree, and the disagreement will surface as a manager being offered a
// rubber the server then refuses. Everything here is a pure function of plain data.

// The rows a format may contain. `type` is a label for the manager, not behaviour - nothing
// branches on single vs double.
export const RUBBER_TYPES = [
  { const: 'single', title: 'Kaksinpeli' },
  { const: 'double', title: 'Nelinpeli' },
]

export const rubberTypeLabel = (type) => {
  const found = RUBBER_TYPES.find((t) => t.const === type)
  return found ? found.title : type || ''
}

const isPositive = (n) => typeof n === 'number' && isFinite(n) && n > 0

// A row is usable only if it asks for a positive number of rubbers of a positive length.
// Anything else is half-typed config and is skipped rather than counted as zero, which would
// otherwise let a format claim rubbers nobody can book.
export const validRows = (format) => {
  const rows = format && Array.isArray(format.rubbers) ? format.rubbers : []
  return rows.filter((r) => r && isPositive(r.count) && isPositive(r.duration))
}

// Total minutes of court the venue sells per tie. This is the number that tells the venue how
// much calendar to open for a league, so it counts every rubber, not distinct lengths.
export const totalCourtTime = (format) =>
  validRows(format).reduce((sum, r) => sum + r.count * r.duration, 0)

// Every rubber the format calls for, one entry each, longest first. The flat list is what both
// the offer count and the span fit work from.
export const flattenRubbers = (format) => {
  const out = []
  validRows(format).forEach((row) => {
    for (let i = 0; i < row.count; i += 1) {
      out.push({ rubberid: row.id, type: row.type, duration: row.duration })
    }
  })
  return out.sort((a, b) => b.duration - a.duration)
}

// How much of the format a tie has already used, keyed by row id. Rubbers whose row no longer
// exists (the venue edited the format after the tie was booked) are simply not counted against
// any row - they cannot be, and refusing to compute an offer because of them would strand the
// tie.
export const bookedByRow = (tie) => {
  const rubbers = tie && Array.isArray(tie.rubbers) ? tie.rubbers : []
  return rubbers.reduce((acc, r) => {
    if (!r || !r.rubberid) return acc
    acc[r.rubberid] = (acc[r.rubberid] || 0) + 1
    return acc
  }, {})
}

/**
 * The menu: which format rows a tie may still book, and how many of each.
 *
 * This is the whole of "no possibility to reserve anything else than the format says" (§4.2).
 * The enduser wizard renders it; the backend re-derives it and refuses anything not in it. A
 * booking names a row id, never a duration, so there is no length for the two sides to
 * disagree about.
 *
 * Pass a tie to get what is left on that tie; pass nothing to get the format's full offer.
 * Rows that are used up are omitted entirely rather than returned with remaining: 0, so the
 * caller cannot accidentally render an exhausted option.
 */
export const remainingOffer = (format, tie) => {
  const booked = bookedByRow(tie)
  return validRows(format)
    .map((row) => ({
      rubberid: row.id,
      type: row.type,
      duration: row.duration,
      count: row.count,
      remaining: row.count - (booked[row.id] || 0),
    }))
    .filter((row) => row.remaining > 0)
}

// Is this row still bookable on this tie? The single question the Phase E guard asks.
export const canBookRow = (format, tie, rubberid) =>
  remainingOffer(format, tie).some((row) => row.rubberid === rubberid)

// Look up a row by id, for turning a booking request into a length.
export const findRow = (format, rubberid) => validRows(format).find((r) => r.id === rubberid) || null

/**
 * How long one tie occupies the venue if it is played on `courts` courts.
 *
 * Longest-processing-time-first: repeatedly put the longest unplaced rubber on whichever court
 * frees up soonest. This is a heuristic, not an optimum, and it is a PLANNING READOUT rather
 * than a constraint - nothing in the feature enforces it, and real availability is fragmented
 * in ways a court count cannot express. Its job is to make "this format needs three hours if
 * you only open two courts" visible while the venue is still typing the format.
 */
export const spanForCourts = (format, courts) => {
  const rubbers = flattenRubbers(format)
  if (!rubbers.length || !isPositive(courts)) return 0
  const freeAt = new Array(Math.floor(courts)).fill(0)
  rubbers.forEach((r) => {
    let earliest = 0
    for (let i = 1; i < freeAt.length; i += 1) {
      if (freeAt[i] < freeAt[earliest]) earliest = i
    }
    freeAt[earliest] += r.duration
  })
  return Math.max.apply(null, freeAt)
}

// The most courts a tie could ever use at once - beyond this, extra courts change nothing.
export const maxUsefulCourts = (format) => flattenRubbers(format).length

/**
 * The span readout as a small table, one column per court count from 1 up to however many
 * courts stop helping. Capped so a format with many rubbers does not render a table nobody
 * reads.
 */
export const spanTable = (format, cap = 6) => {
  const useful = Math.min(maxUsefulCourts(format), cap)
  const out = []
  for (let courts = 1; courts <= useful; courts += 1) {
    out.push({ courts, span: spanForCourts(format, courts) })
  }
  return out
}

// "2 h 45 min" / "45 min". Durations here are always whole minutes.
export const formatMinutes = (minutes) => {
  if (!isPositive(minutes)) return '-'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (!h) return m + ' min'
  if (!m) return h + ' h'
  return h + ' h ' + m + ' min'
}

// One-line summary for the format list: "3 x Kaksinpeli 1 h 30 min, 1 x Nelinpeli 1 h 15 min".
export const describeFormat = (format) => {
  const rows = validRows(format)
  if (!rows.length) return 'Ei otteluita'
  return rows
    .map((r) => r.count + ' x ' + rubberTypeLabel(r.type) + ' ' + formatMinutes(r.duration))
    .join(', ')
}
